import React, { useCallback, useEffect, useState } from "react"
import clsx from "classnames"
import i18n from "lib/i18n"
import PropTypes from "prop-types"
import errorsPropType from "components/PropTypes/errorsPropType"
import DonationFrequency from "components/DonationFrequency"
import AmountButton from "components/AmountButton"
import CustomAmountButton from "components/CustomAmountButton"
import Analytics from "lib/Analytics"
import { chunk, flatten, isEqual } from "underscore"

// if number of amounts is even, use 2, if odd, use 3
const MAX_AMOUNT_COLUMNS = [2, 3]
const GA_AMOUNT_LABEL = "Amount"
const GA_CUSTOM_AMOUNT_LABEL = "Custom Amount"

const AmountDetails = ({
  active = true,
  amount = "100",
  currencySign = "$",
  defaultAmounts = {
    once: "100",
    monthly: "20",
  },
  errors = {},
  frequency = "once",
  initialFrequency = "once",
  onChange = () => {},
  suggestedAmounts = ["10", "30", "100"],
}) => {
  const [custom, setCustom] = useState(suggestedAmounts.indexOf(amount) === -1)

  // Be careful about changing this!
  //
  // This is here to deal with the case where the default amount for a frequency is not one of the
  // suggested values. When the user changes to that frequency, we need to recognise that and change
  // the amount to "custom".
  useEffect(() => {
    if (!custom && suggestedAmounts.indexOf(amount) === -1) {
      setCustom(true)
    }
  }, [custom, suggestedAmounts, amount])

  const setAmount = useCallback(
    (newAmount) => {
      Analytics.trackFormEvent("Donation Form", "Completed", GA_AMOUNT_LABEL, newAmount)
      setCustom(false)
      onChange({ amount: newAmount })
    },
    [onChange],
  )

  const setCustomAmount = useCallback(
    (newAmount) => {
      Analytics.trackFormEvent("Donation Form", "Completed", GA_CUSTOM_AMOUNT_LABEL, newAmount)
      setCustom(true)
      onChange({ amount: newAmount })
    },
    [onChange],
  )

  const setFrequency = useCallback(
    (newFrequency) => {
      const defaultAmount = defaultAmounts[newFrequency]
      setCustom(false)
      onChange({ amount: defaultAmount, frequency: newFrequency })
    },
    [onChange, defaultAmounts],
  )

  const amountSelected = useCallback(
    (currentAmount) => !custom && currentAmount === amount,
    [custom, amount],
  )

  const customAmount = useCallback((currentAmount) => (custom ? currentAmount : ""), [custom])

  const label = (currentAmount) => currencySign + currentAmount
  const error = () => {
    if (errors.amount) {
      Analytics.trackFormEvent("Donation Form", "Validation Error", GA_CUSTOM_AMOUNT_LABEL)
      return <span className="text-red-700">{errors.amount}</span>
    }
    return ""
  }

  const renderAmountButtons = () => {
    const numAmounts = suggestedAmounts.length
    const chunkedAmounts = chunk(suggestedAmounts, MAX_AMOUNT_COLUMNS[numAmounts % 2])

    return flatten(
      chunkedAmounts.map((amounts) =>
        amounts.map((buttonAmount) => (
          <AmountButton
            active={active}
            key={buttonAmount}
            label={label(buttonAmount)}
            secondaryLabel={
              amountSelected(buttonAmount) && frequency === "monthly"
                ? i18n.t("donation.button.per_month")
                : null
            }
            onClick={() => setAmount(buttonAmount)}
            selected={amountSelected(buttonAmount)}
            siblingsCount={amounts.length - 1}
          />
        )),
      ),
    )
  }

  return (
    <div className="mb-6">
      <h6 className="font-sans-heading text-xl font-bold">
        {i18n.t("donation.heading.amount_and_frequency")}
      </h6>
      <input id="donation_amount" type="hidden" value={amount} name="donation[amount]" />
      <input id="donation_frequency" type="hidden" value={frequency} name="donation[frequency]" />

      <DonationFrequency
        active={active}
        initial={initialFrequency}
        onChange={setFrequency}
        selected={frequency}
      />

      <label
        htmlFor="customAmount"
        className={clsx("mt-5 block font-sans text-xs font-bold text-gray-500", {
          "text-red-700": !!errors.amount,
        })}
      >
        {frequency === "monthly"
          ? i18n.t("donation.label.amount_per_month")
          : i18n.t("donation.label.amount")}
      </label>

      <div className="-mx-1 mt-0.5 mb-4">
        {renderAmountButtons()}
        <CustomAmountButton
          active={active}
          amount={customAmount(amount)}
          error={!!errors.amount}
          label={currencySign}
          secondaryLabel={frequency === "monthly" ? i18n.t("donation.button.per_month") : null}
          onClick={setCustomAmount}
          onInput={setCustomAmount}
          selected={custom}
        />
      </div>
      {error()}
    </div>
  )
}

AmountDetails.propTypes = {
  active: PropTypes.bool,
  amount: PropTypes.string,
  classes: PropTypes.shape({
    amountDetails: PropTypes.string,
    error: PropTypes.string,
    label: PropTypes.string,
  }),
  currencySign: PropTypes.string,
  defaultAmounts: PropTypes.shape({ once: PropTypes.string, monthly: PropTypes.string }),
  errors: errorsPropType,
  frequency: PropTypes.oneOf(["unfilled", "once", "monthly"]),
  initialFrequency: PropTypes.oneOf(["once", "monthly"]),
  onChange: PropTypes.func,
  suggestedAmounts: PropTypes.arrayOf(PropTypes.string),
}

export default React.memo(AmountDetails, (prevProps, nextProps) => isEqual(prevProps, nextProps))
